<ng-template #mainContentTpl>
  <div class="w-full h-full relative bg-body-background flex flex-col">
    @if (isLoading()) {
      <div class="flex items-center justify-center p-12 h-full w-full">
        <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
      </div>
    } @else if (error()) {
      <div class="flex items-center justify-center h-full w-full">
        <div
          class="p-6 text-center text-danger bg-danger/5 border border-danger/20 rounded-xl max-w-md shadow-sm"
        >
          <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
          <p class="font-medium m-0">{{ error() }}</p>
        </div>
      </div>
    } @else {
      <rapider-html-preview
        [rawCode]="liveCode() !== undefined ? liveCode()! : htmlContent()"
        [sandboxTitle]="displayTitle()"
        [enableEditorSdk]="enableEditorSdk()"
        [allowSameOrigin]="effectiveAllowSameOrigin()"
        [displayBrowserHeader]="displayBrowserHeader()"
        [assetBaseUrl]="assetBaseUrl()"
        [appTheme]="appTheme()"
        (onIframeMessage)="handleIframeMessage($event)"
        (onIframeLoad)="onIframeLoad.emit($event)"
      >
      </rapider-html-preview>
    }
  </div>
</ng-template>

<nz-splitter
  [nzLayout]="pageSplitterLayout()"
  class="w-full h-full flex-1 min-h-0 bg-body-background transition-all duration-300"
  [ngClass]="{
    '[&_.ant-splitter-bar]:!hidden': !isPageSplitterVisible(),
    '[&_.ant-splitter-bar]:pointer-events-none':
      !pageSplitterResizable() && isPageSplitterVisible(),
  }"
>
  <nz-splitter-panel class="w-full h-full relative min-h-0 min-w-0 flex flex-col">
    <div class="w-full h-full relative bg-body-background flex flex-col flex-1 min-h-0">
      @if (isLoading()) {
        <div class="flex items-center justify-center p-12 h-full w-full">
          <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
        </div>
      } @else if (error()) {
        <div class="flex items-center justify-center h-full w-full">
          <div
            class="p-6 text-center text-danger bg-danger/5 border border-danger/20 rounded-xl max-w-md shadow-sm"
          >
            <i class="fas fa-exclamation-triangle text-3xl mb-3"></i>
            <p class="font-medium m-0">{{ error() }}</p>
          </div>
        </div>
      } @else {
        <rapider-html-preview #previewRef
          class="w-full h-full flex-1 min-h-0 flex flex-col relative"
          [rawCode]="liveCode() !== undefined ? liveCode()! : htmlContent()"
          [sandboxTitle]="displayTitle()"
          [enableEditorSdk]="enableEditorSdk()"
          [allowSameOrigin]="effectiveAllowSameOrigin()"
          [routeParams]="routeParams()"
          [displayBrowserHeader]="displayBrowserHeader()"
          [assetBaseUrl]="assetBaseUrl()"
          [appTheme]="appTheme()"
          (onIframeMessage)="handleIframeMessage($event, previewRef)"
          (onEditorEvent)="onEditorEvent.emit($event)"
          (onIframeLoad)="onIframeLoad.emit($event)"
        >
        </rapider-html-preview>
      }
    </div>
  </nz-splitter-panel>

  <nz-splitter-panel
    [nzSize]="isPageSplitterVisible() ? pageSplitterDefaultSize() : 0"
    [nzMin]="isPageSplitterVisible() ? pageSplitterMin() || 0 : 0"
    [nzMax]="isPageSplitterVisible() ? pageSplitterMax() || '100%' : 0"
    class="relative h-full min-h-0 min-w-0 bg-body-background"
    [ngClass]="!isPageSplitterVisible() ? 'overflow-hidden border-none' : ''"
  >
    @if (isPageSplitterVisible()) {
      <div
        class="flex flex-col h-full w-full bg-body-background border-l border-primary-border overflow-hidden @container"
      >
        <header
          class="flex items-center justify-between p-3 border-b border-secondary-border bg-component-header shrink-0"
        >
          <div class="flex items-center gap-2">
            @if (pageSplitterIcon()) {
              <i [class]="pageSplitterIcon() + ' text-secondary-text'"></i>
            }
            @if (pageSplitterTitle()) {
              <span class="font-bold text-sm text-primary-text">{{
                pageSplitterTitle()
              }}</span>
            }
          </div>
          <button
            (click)="closePageSplitter()"
            class="w-6 h-6 rounded flex items-center justify-center text-secondary-text hover:text-danger hover:bg-danger/10 focus:outline-none transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div
          class="flex-1 min-h-0 w-full relative overflow-y-auto custom-scrollbar"
        >
          @if (isPageSplitterLoading()) {
            <div class="flex items-center justify-center p-12 w-full h-full">
              <div class="flex flex-col items-center gap-4">
                <i
                  class="fas fa-circle-notch fa-spin text-4xl text-primary"
                ></i>
                <span class="text-secondary-text font-medium text-base"
                  >Loading pane...</span
                >
              </div>
            </div>
          } @else if (pageSplitterType() === 'AppPage' && pageSplitterId()) {
            <div class="w-full h-full p-6 relative overflow-hidden">
              <rapider-html-url-preview
                class="block w-full h-full"
                [htmlUrl]="pageSplitterUrl() || ''"
                [appName]="appName()"
                [pagesManifest]="pagesManifest()"
                [routeParams]="pageSplitterPayload()"
                [dataModels]="dataModels()"
                [assetBaseUrl]="assetBaseUrl()"
                [appTheme]="appTheme()"
                [testMode]="testMode()"
                [allowSameOrigin]="effectiveAllowSameOrigin()"
                [displayBrowserHeader]="false"
              >
              </rapider-html-url-preview>
            </div>
          } @else if (
            pageSplitterType() === 'AppDynamicPage' && pageSplitterConfig()
          ) {
            <rapider-dynamic-page-renderer
              [pageConfig]="pageSplitterConfig()!"
              [initialPayload]="pageSplitterPayload()"
              [standaloneMode]="true"
              [expandToContentHeight]="true"
            >
            </rapider-dynamic-page-renderer>
          }
        </div>
      </div>
    }
  </nz-splitter-panel>
</nz-splitter>

<nz-modal
  [nzVisible]="isPageModalVisible()"
  (nzVisibleChange)="isPageModalVisible.set($event)"
  [nzTitle]="pageModalTitle()"
  [nzWidth]="pageModalWidth()"
  [nzFooter]="null"
  (nzOnCancel)="closePageModal()"
>
  <ng-container *nzModalContent>
    @if (isPageModalLoading()) {
      <div class="flex items-center justify-center p-12 w-full min-h-[300px]">
        <div class="flex flex-col items-center gap-4">
          <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
          <span class="text-secondary-text font-medium text-base"
            >Loading modal...</span
          >
        </div>
      </div>
    } @else if (pageModalType() === 'AppPage' && pageModalId()) {
      <div class="w-full relative h-[70vh] p-6 overflow-hidden">
        <rapider-html-url-preview
          class="block w-full h-full"
          [htmlUrl]="pageModalUrl() || ''"
          [appName]="appName()"
          [pagesManifest]="pagesManifest()"
          [routeParams]="pageModalPayload()"
          [dataModels]="dataModels()"
          [assetBaseUrl]="assetBaseUrl()"
          [appTheme]="appTheme()"
          [testMode]="testMode()"
          [allowSameOrigin]="effectiveAllowSameOrigin()"
          [displayBrowserHeader]="false"
        >
        </rapider-html-url-preview>
      </div>
    } @else if (pageModalType() === 'AppDynamicPage' && pageModalConfig()) {
      <div
        class="w-full relative max-h-[80vh] overflow-y-auto custom-scrollbar"
      >
        <rapider-dynamic-page-renderer
          [pageConfig]="pageModalConfig()!"
          [initialPayload]="pageModalPayload()"
          [standaloneMode]="true"
          [expandToContentHeight]="true"
        >
        </rapider-dynamic-page-renderer>
      </div>
    }
  </ng-container>
</nz-modal>

<nz-drawer
  [nzVisible]="isPageDrawerVisible()"
  (nzVisibleChange)="isPageDrawerVisible.set($event)"
  [nzTitle]="pageDrawerTitle()"
  [nzPlacement]="pageDrawerPlacement()"
  [nzWidth]="effectiveDrawerWidth()"
  (nzOnClose)="closePageDrawer()"
>
  <ng-container *nzDrawerContent>
    @if (isPageDrawerLoading()) {
      <div class="flex items-center justify-center p-12 w-full h-full">
        <div class="flex flex-col items-center gap-4">
          <i class="fas fa-circle-notch fa-spin text-4xl text-primary"></i>
          <span class="text-secondary-text font-medium text-base"
            >Loading drawer...</span
          >
        </div>
      </div>
    } @else if (pageDrawerType() === 'AppPage' && pageDrawerId()) {
      <div class="w-full h-full p-6 relative overflow-hidden">
        <rapider-html-url-preview
          class="block w-full h-full"
          [htmlUrl]="pageDrawerUrl() || ''"
          [appName]="appName()"
          [pagesManifest]="pagesManifest()"
          [routeParams]="pageDrawerPayload()"
          [dataModels]="dataModels()"
          [assetBaseUrl]="assetBaseUrl()"
          [appTheme]="appTheme()"
          [testMode]="testMode()"
          [allowSameOrigin]="effectiveAllowSameOrigin()"
          [displayBrowserHeader]="false"
        >
        </rapider-html-url-preview>
      </div>
    } @else if (pageDrawerType() === 'AppDynamicPage' && pageDrawerConfig()) {
      <div class="w-full h-full relative overflow-y-auto custom-scrollbar">
        <rapider-dynamic-page-renderer
          [pageConfig]="pageDrawerConfig()!"
          [initialPayload]="pageDrawerPayload()"
          [standaloneMode]="true"
          [expandToContentHeight]="true"
        >
        </rapider-dynamic-page-renderer>
      </div>
    }
  </ng-container>
</nz-drawer>
